import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	changeRollset,
	validatePassChange,
} from "../repositories/MillSetupRepository";

interface UseChangeRollsetOptions {
	onSuccess?: () => void;
	onError?: (error: Error) => void;
	onWarning?: (message: string) => Promise<boolean>;
}

export const useChangeRollset = ({
	onSuccess,
	onError,
	onWarning,
}: UseChangeRollsetOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (params: {
			standId: number;
			newRollsetId: number | null;
			newPassPosition: number | null;
			productSizeId?: number;
		}) => {
			if (params.newRollsetId && params.newPassPosition) {
				const result = await validatePassChange({
					standId: params.standId,
					rollsetId: params.newRollsetId,
					passPosition: params.newPassPosition,
					productSizeId: params.productSizeId,
				});

				if (result === "WARNING") {
					const confirmed = onWarning
						? await onWarning(
								"Pass does not match the product mill setup. Continue?",
							)
						: false;
					if (!confirmed) {
						throw new Error("Rollset change cancelled by user");
					}
				}
			}

			await changeRollset({
				standId: params.standId,
				newRollsetId: params.newRollsetId,
				newPassPosition: params.newPassPosition,
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["millSetup"] });
			onSuccess?.();
		},
		onError: (error: Error) => {
			if (error.message !== "Rollset change cancelled by user") {
				onError?.(error);
			}
		},
	});

	return {
		changeRollset: mutation.mutate,
		isChangingRollset: mutation.isPending,
	};
};
